/**
 * Centralizes Firestore error handling so every write site reacts the
 * same way to quota/permission/offline failures.
 *
 * Listeners (the AdBlockerBanner-style infra) can subscribe to global
 * events fired here. Sentry is notified for severe codes so you find
 * out before users complain.
 */
import { Sentry } from './sentry';

const listeners = new Set();

/** Subscribe to firestore-write-failure events. Returns an unsubscribe. */
export function onFirestoreError(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(payload) {
  listeners.forEach(fn => { try { fn(payload); } catch {} });
}

/** Quota-related Firestore error codes. */
const QUOTA_CODES = new Set([
  'resource-exhausted',
  'unavailable',
]);

/** "User can't write here" codes — usually a rules problem, not a quota one. */
const PERMISSION_CODES = new Set([
  'permission-denied',
  'unauthenticated',
]);

/**
 * Wraps a Firestore write (or any promise) and classifies the error.
 * Returns the original promise; on rejection, fires a global event AND
 * reports to Sentry (rate-limited to avoid spamming).
 *
 * Usage:
 *   handleFirestoreWrite(() => updateDoc(doc(db, 'users', uid), { foo: 1 }))
 */
let lastSentryReport = 0;
const SENTRY_REPORT_THROTTLE_MS = 60 * 1000;

export function handleFirestoreWrite(promiseFactory, context = {}) {
  return Promise.resolve()
    .then(promiseFactory)
    .catch(err => {
      const code = err?.code || '';
      let category = 'unknown';
      if (QUOTA_CODES.has(code)) category = 'quota';
      else if (PERMISSION_CODES.has(code)) category = 'permission';
      else if (code.startsWith('cancelled') || code === 'aborted') category = 'cancelled';

      emit({ code, category, message: err?.message, context });

      // Rate-limit Sentry reports so a chain of failures doesn't blow up
      // your free-tier event quota too.
      const now = Date.now();
      if (category !== 'cancelled' && (now - lastSentryReport) > SENTRY_REPORT_THROTTLE_MS) {
        lastSentryReport = now;
        try {
          Sentry.captureException(err, {
            tags: { firestore_error_category: category, firestore_error_code: code },
            extra: context,
          });
        } catch {}
      }

      throw err;
    });
}
